import { Card, Space } from 'antd';
import type { CardProps } from 'antd';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Карточка списка с закреплённой шапкой.
 *
 * Проблема, которую она решает: в длинных таблицах поиск и фильтры уезжали
 * вверх при прокрутке, и чтобы сменить фильтр, приходилось возвращаться к
 * началу страницы. Теперь строка фильтров и заголовки колонок остаются на
 * месте.
 *
 * Высота шапки не зашита числом: при узком окне фильтры переносятся на вторую
 * строку, и фиксированное значение оставило бы заголовки таблицы либо с
 * разрывом, либо под панелью. Поэтому она измеряется и передаётся таблице
 * через контекст.
 */

/** Высота шапки приложения — под ней прилипает панель фильтров. */
export const APP_HEADER_HEIGHT = 64;
/**
 * Зазор между шапкой приложения и карточкой — отступ содержимого в AppLayout.
 * Шапка карточки прилипает с этим же зазором (см. styles.css), поэтому и
 * заголовки таблицы под ней стоят ниже на столько же.
 */
const CONTENT_GAP = 24;

interface TableCardApi {
  /** Отступ сверху для заголовков таблицы: шапка приложения + панель фильтров. */
  offsetHeader: number;
  /**
   * Место в шапке под кнопку «Отчёт» по выбранным строкам. Таблица рисует
   * её сюда порталом, чтобы кнопка стояла в одном ряду с фильтрами.
   * null — таблица вне карточки.
   */
  selectionSlot: HTMLElement | null;
  /** Заголовок карточки — название списка для выгрузки. */
  title: string | null;
}

const TableCardContext = createContext<TableCardApi>({
  offsetHeader: APP_HEADER_HEIGHT,
  selectionSlot: null,
  title: null,
});

export function useTableCard(): TableCardApi {
  return useContext(TableCardContext);
}

interface TableCardProps extends Omit<CardProps, 'children'> {
  children: ReactNode;
}

export function TableCard({ children, extra, ...cardProps }: TableCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [headHeight, setHeadHeight] = useState(0);
  const [selectionSlot, setSelectionSlot] = useState<HTMLElement | null>(null);
  const title = typeof cardProps.title === 'string' ? cardProps.title : null;

  useEffect(() => {
    const head = ref.current?.querySelector<HTMLElement>(':scope > .ant-card > .ant-card-head');
    if (!head) return;

    const observer = new ResizeObserver(([entry]) => {
      setHeadHeight(entry.contentRect.height);
    });
    observer.observe(head);
    setHeadHeight(head.getBoundingClientRect().height);

    return () => observer.disconnect();
  }, []);

  const api = useMemo<TableCardApi>(
    () => ({
      offsetHeader: APP_HEADER_HEIGHT + CONTENT_GAP + headHeight,
      selectionSlot,
      title,
    }),
    [headHeight, selectionSlot, title],
  );

  // Пустой extra не рендерим: Card иначе отводит под него место в шапке.
  // Место под панель выбора есть только там, где у карточки уже есть шапка;
  // без неё таблица покажет панель над собой.
  const headExtra =
    extra || cardProps.title ? (
      <Space size="small">
        <span ref={setSelectionSlot} className="tms-selection-slot" />
        {extra}
      </Space>
    ) : undefined;

  return (
    <div ref={ref} className="tms-table-card">
      <TableCardContext.Provider value={api}>
        <Card {...cardProps} extra={headExtra}>
          {children}
        </Card>
      </TableCardContext.Provider>
    </div>
  );
}
